import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Tags, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const NONE = "__none__";

export const CustomCategoriesSection = () => {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingName, setSavingName] = useState("");

  useEffect(() => {
    Promise.all([api.get("/custom-categories"), api.get("/categories"), api.get("/products")])
      .then(([r1, r2, r3]) => {
        setItems(r1.data.data);
        setCats(r2.data.data.filter((c) => (c.name || "").toLowerCase() !== "custom"));
        setProducts(r3.data.data || r3.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const subsOf = (catName) => {
    const c = cats.find((x) => x.name === catName);
    return (c?.subCategories || []).map((s) => s?.name || s).filter(Boolean);
  };

  const productsOf = (catName) => products.filter((p) => p.category === catName);

  const save = async (name, category, sub_category, sku) => {
    setSavingName(name);
    try {
      await api.post("/custom-categories", { name, category, sub_category, sku });
      setItems((prev) => prev.map((it) => (it.name === name ? { ...it, category, sub_category, sku } : it)));
      toast.success(category ? `"${name}" → ${category}${sku ? ` · ${sku}` : ""}` : "Custom'a geri alındı");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSavingName("");
    }
  };

  const assigned = items.filter((i) => i.category).length;

  return (
    <div className="border border-stone-200 bg-white p-4" data-testid="custom-categories-section">
      <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#1C4E35]">
        <Tags size={14} /> Custom Ürün Kategorileri
      </p>
      <p className="mt-1 text-xs text-stone-500">
        Portaldan "Custom" kategorisiyle gelen katalog dışı kalemlere kategori atayın; isterseniz "Birebir Ürün" ile katalogdaki gerçek ürüne eşleyin — eşlenen kalem tüm liste ve raporlarda o ürünün altında toplanır. ({assigned}/{items.length} atandı)
      </p>
      {loading ? (
        <p className="mt-3 flex items-center gap-2 text-xs text-stone-400"><Loader2 size={13} className="animate-spin" /> Yükleniyor…</p>
      ) : items.length === 0 ? (
        <p className="mt-3 text-xs text-stone-400">Custom kategorili sipariş kalemi yok.</p>
      ) : (
        <table className="mt-3 w-full text-left text-xs">
          <thead>
            <tr className="border-b border-stone-200 text-[10px] uppercase tracking-wider text-stone-400">
              <th className="py-1.5 pr-3">Ürün Adı</th>
              <th className="py-1.5 pr-3 text-right">Sipariş / Adet</th>
              <th className="py-1.5 pr-3">Kategori</th>
              <th className="py-1.5 pr-3">Alt Kategori</th>
              <th className="py-1.5">Birebir Ürün</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const subs = subsOf(it.category);
              const prods = productsOf(it.category);
              const rowId = it.name.slice(0, 30).replace(/\s+/g, "_");
              return (
                <tr key={it.name} className={`border-b border-stone-100 ${it.category ? "" : "bg-amber-50/50"}`} data-testid={`custom-cat-row-${rowId}`}>
                  <td className="max-w-sm py-1.5 pr-3 font-medium text-[#0A0D0B]">{it.name}</td>
                  <td className="py-1.5 pr-3 text-right text-stone-500">{it.orderCount} / {it.qty}</td>
                  <td className="py-1.5 pr-3">
                    <Select
                      value={it.category || NONE}
                      disabled={savingName === it.name}
                      onValueChange={(v) => save(it.name, v === NONE ? "" : v, "", "")}
                    >
                      <SelectTrigger className="h-8 w-52 rounded-sm border-stone-300 text-xs" data-testid={`custom-cat-select-${rowId}`}>
                        <SelectValue placeholder="Kategori seç" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>— Atanmadı (Custom) —</SelectItem>
                        {cats.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-1.5 pr-3">
                    {it.category && subs.length > 0 ? (
                      <Select
                        value={it.sub_category || NONE}
                        disabled={savingName === it.name}
                        onValueChange={(v) => save(it.name, it.category, v === NONE ? "" : v, it.sku || "")}
                      >
                        <SelectTrigger className="h-8 w-44 rounded-sm border-stone-300 text-xs" data-testid={`custom-subcat-select-${rowId}`}>
                          <SelectValue placeholder="Alt kategori" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>— Yok —</SelectItem>
                          {subs.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-stone-300">—</span>
                    )}
                  </td>
                  <td className="py-1.5">
                    {it.category && prods.length > 0 ? (
                      <Select
                        value={it.sku || NONE}
                        disabled={savingName === it.name}
                        onValueChange={(v) => save(it.name, it.category, it.sub_category || "", v === NONE ? "" : v)}
                      >
                        <SelectTrigger className="h-8 w-72 rounded-sm border-stone-300 text-xs" data-testid={`custom-product-select-${rowId}`}>
                          <SelectValue placeholder="Katalog ürünü seç (opsiyonel)" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          <SelectItem value={NONE}>— Birebir eşleme yok —</SelectItem>
                          {prods.map((p) => (
                            <SelectItem key={p.sku} value={p.sku}>
                              {p.name} <span className="text-stone-400">({p.sku})</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-stone-300">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};
